import type { CSSProperties, FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { AdvertSceneId, AdvertSceneProps } from '@/types'

import { Fragment } from 'react'

import {
    ArrowRight,
    Building2,
    CalendarCheck,
    Check,
    RotateCcw,
    Store
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { BOLT_PATH, LogoDescriptor, LogoMark, LogoWordmark } from '@/components/brand/logo'
import { ADVERT_MEDIA } from '@/constants/advert'

// Sizes inside the advert use cqw (a percentage of the advert's width), so the
// whole composition scales like a video at every screen size. Classes without a
// prefix are the portrait phone layout; sm: classes are the 16:9 layout.

const timing = (delay: number, duration?: number, ease?: string): CSSProperties => {
    const style: Record<string, string> = { '--ad-delay': `${delay}ms` }

    if (duration) style['--ad-dur'] = `${duration}ms`
    if (ease) style['--ad-ease'] = ease

    return style as CSSProperties
}

interface SceneFrameProps {
    duration: number
    className?: string
    children: ReactNode
}

// Wraps a scene and fades it out in its last moments.
const SceneFrame: FC<SceneFrameProps> = ({ duration, className, children }): ReactNode => {
    return (
        <div
            className={cn('ad-a ad-scene-out absolute inset-0', className)}
            style={timing(duration - 450, 450, 'ease-in')}
        >
            {children}
        </div>
    )
}

interface WordsProps {
    text: string
    start: number
    step?: number
}

// Reveals a line word by word.
const Words: FC<WordsProps> = ({ text, start, step = 90 }): ReactNode => {
    const words = text.split(' ')

    return (
        <>
            {words.map((word, index) => (
                <Fragment key={`${word}-${index}`}>
                    <span className='ad-a ad-rise inline-block' style={timing(start + index * step)}>
                        {word}
                    </span>
                    {index < words.length - 1 && ' '}
                </Fragment>
            ))}
        </>
    )
}

interface ChipProps {
    icon: LucideIcon
    label: string
    delay: number
}

const Chip: FC<ChipProps> = ({ icon: Icon, label, delay }): ReactNode => {
    return (
        <span
            className='ad-a ad-wipe inline-flex items-center gap-[1.4cqw] rounded-full bg-volt px-[2.8cqw] py-[1.2cqw] text-[length:max(10px,2.8cqw)] font-bold uppercase tracking-[0.12em] text-ink sm:gap-[0.7cqw] sm:px-[1.5cqw] sm:py-[0.65cqw] sm:text-[length:max(10px,1.3cqw)]'
            style={timing(delay, 600)}
        >
            <Icon className='size-[1.2em]' aria-hidden='true' />
            {label}
        </span>
    )
}

const IntroScene: FC<AdvertSceneProps> = ({ duration }): ReactNode => {
    return (
        <SceneFrame duration={duration} className='flex flex-col items-center justify-center px-[6cqw] text-center'>
            <div
                aria-hidden='true'
                className='ad-a ad-flash absolute inset-0 bg-volt/20'
                style={timing(120, 800, 'ease-out')}
            />
            <div
                aria-hidden='true'
                className='ad-a ad-fade absolute left-1/2 top-1/2 size-[80cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-volt/10 blur-[10cqw] sm:size-[56cqw]'
                style={timing(200, 1400)}
            />

            <svg viewBox='0 0 48 100' aria-hidden='true' className='relative h-[17cqw] w-auto overflow-visible sm:h-[10cqw]'>
                <path
                    d={BOLT_PATH}
                    pathLength={1}
                    className='ad-a ad-draw fill-none stroke-volt [stroke-linejoin:round] [stroke-width:2.5]'
                    style={timing(0, 650, 'ease-in-out')}
                />
                <path d={BOLT_PATH} className='ad-a ad-fade fill-volt' style={timing(550, 300)} />
            </svg>

            <p
                className='ad-a ad-rise relative mt-[3cqw] font-heading text-[length:6cqw] font-semibold text-paper/70 sm:mt-[2cqw] sm:text-[length:3.4cqw]'
                style={timing(700)}
            >
                Got a
            </p>

            <p
                className='ad-a ad-slam relative whitespace-nowrap font-heading text-[length:12cqw] font-extrabold leading-none tracking-[-0.03em] text-volt sm:text-[length:9cqw]'
                style={timing(900, 700)}
            >
                business?
            </p>

            <p
                className='ad-a ad-rise relative mt-[4cqw] max-w-[78cqw] text-balance text-[length:max(12px,3.9cqw)] font-medium leading-snug text-paper/80 sm:mt-[2.6cqw] sm:max-w-[50cqw] sm:text-[length:max(12px,1.9cqw)]'
                style={timing(1600)}
            >
                It deserves a website that works as hard as you do.
            </p>
        </SceneFrame>
    )
}

const LocalScene: FC<AdvertSceneProps> = ({ duration }): ReactNode => {
    return (
        <SceneFrame duration={duration} className='flex flex-col items-start justify-end p-[6cqw]'>
            <Chip icon={Store} label='Local businesses' delay={200} />

            <p className='mt-[3cqw] max-w-[84cqw] text-balance font-heading text-[length:8.4cqw] font-bold leading-[1.04] tracking-[-0.02em] text-paper sm:mt-[1.8cqw] sm:max-w-[60cqw] sm:text-[length:5.2cqw]'>
                <Words text='The shop on your high street,' start={450} />
            </p>

            <p
                className='ad-a ad-fade mt-[3cqw] text-[length:max(10px,3cqw)] font-semibold uppercase tracking-[0.16em] text-paper/75 sm:mt-[1.8cqw] sm:text-[length:max(10px,1.35cqw)]'
                style={timing(1500)}
            >
                Cafés · Salons · Trades · Shops
            </p>
        </SceneFrame>
    )
}

const BigScene: FC<AdvertSceneProps> = ({ duration }): ReactNode => {
    return (
        <SceneFrame duration={duration} className='flex flex-col items-end justify-end p-[6cqw] text-right'>
            <Chip icon={Building2} label='Big companies' delay={200} />

            <p className='mt-[3cqw] max-w-[84cqw] text-balance font-heading text-[length:8.4cqw] font-bold leading-[1.04] tracking-[-0.02em] text-paper sm:mt-[1.8cqw] sm:max-w-[60cqw] sm:text-[length:5.2cqw]'>
                <Words text='or the brand in every city.' start={450} />
            </p>

            <p
                className='ad-a ad-fade mt-[3cqw] text-[length:max(10px,3cqw)] font-semibold uppercase tracking-[0.16em] text-paper/75 sm:mt-[1.8cqw] sm:text-[length:max(10px,1.35cqw)]'
                style={timing(1500)}
            >
                Head offices · National brands · Growing teams
            </p>
        </SceneFrame>
    )
}

const BothScene: FC<AdvertSceneProps> = ({ duration }): ReactNode => {
    const cardClass = 'relative aspect-[4/3] w-[36cqw] overflow-hidden rounded-[2.4cqw] border border-paper/15 shadow-2xl shadow-black/50 sm:w-[24cqw] sm:rounded-[1.4cqw]'
    const captionClass = 'absolute bottom-[2cqw] rounded-full bg-ink/80 px-[2cqw] py-[0.8cqw] text-[length:max(10px,2.6cqw)] font-bold text-paper sm:bottom-[1.1cqw] sm:px-[1.1cqw] sm:py-[0.45cqw] sm:text-[length:max(10px,1.2cqw)]'

    return (
        <SceneFrame duration={duration} className='flex flex-col items-center justify-center gap-[8cqw] px-[6cqw] text-center sm:gap-[4.5cqw]'>
            <p className='font-heading text-[length:7cqw] font-extrabold leading-[1.1] tracking-[-0.02em] text-paper sm:text-[length:5.2cqw]'>
                <Words text='We build websites' start={150} />
                <br />
                <Words text='for' start={430} />
                {' '}
                <span className='ad-a ad-slam relative inline-block text-volt' style={timing(620, 650)}>
                    both.
                    <svg viewBox='0 0 120 16' aria-hidden='true' className='absolute left-0 top-[96%] h-auto w-full overflow-visible'>
                        <path
                            d='M3 11 32 4l18 9 26-9 18 8 21-7'
                            pathLength={1}
                            className='ad-a ad-draw fill-none stroke-volt [stroke-linecap:round] [stroke-linejoin:round] [stroke-width:5]'
                            style={timing(1100, 500, 'ease-out')}
                        />
                    </svg>
                </span>
            </p>

            <div className='relative flex items-center justify-center'>
                <figure className={cn('ad-a ad-card-l', cardClass)} style={timing(950, 900)}>
                    <img
                        src={ADVERT_MEDIA.local.poster}
                        alt=''
                        decoding='async'
                        className='size-full object-cover object-[70%_60%]'
                    />
                    <figcaption className={cn(captionClass, 'left-[2cqw] sm:left-[1.1cqw]')}>
                        Local
                    </figcaption>
                </figure>

                <div className='ad-a ad-pop relative z-10 -mx-[4cqw] sm:-mx-[2.6cqw]' style={timing(1550, 600)}>
                    <LogoMark className='size-[15cqw] drop-shadow-[0_1.5cqw_3cqw_rgba(0,0,0,0.55)] sm:size-[9cqw]' />
                </div>

                <figure className={cn('ad-a ad-card-r', cardClass)} style={timing(1100, 900)}>
                    <img
                        src={ADVERT_MEDIA.big.poster}
                        alt=''
                        decoding='async'
                        className='size-full object-cover'
                    />
                    <figcaption className={cn(captionClass, 'right-[2cqw] sm:right-[1.1cqw]')}>
                        Big
                    </figcaption>
                </figure>
            </div>
        </SceneFrame>
    )
}

const BrowserMockup: FC = (): ReactNode => {
    return (
        <div className='ad-a ad-zoom relative w-[82cqw] self-center @container sm:w-full' style={timing(100, 800)}>
            <div className='overflow-hidden rounded-[3cqw] border border-paper/15 bg-ink-soft shadow-2xl shadow-black/60'>
                <div className='flex h-[7cqw] items-center gap-[1.4cqw] border-b border-paper/10 px-[2.8cqw]'>
                    <span className='size-[1.8cqw] rounded-full bg-paper/25' />
                    <span className='size-[1.8cqw] rounded-full bg-paper/25' />
                    <span className='size-[1.8cqw] rounded-full bg-volt' />

                    <div className='ml-[2cqw] flex h-[4cqw] flex-1 items-center rounded-full bg-paper/7 px-[2.4cqw]'>
                        <span
                            className='ad-a ad-wipe whitespace-nowrap font-mono text-[length:max(8px,2.3cqw)] text-paper/70'
                            style={timing(450, 900, 'steps(20, end)')}
                        >
                            www.yourbusiness.com
                        </span>
                        <span className='ad-blink ml-[0.4cqw] h-[2.6cqw] w-[0.3cqw] bg-volt' />
                    </div>
                </div>

                <div className='relative aspect-[16/10] p-[3.5cqw]'>
                    <div className='ad-a ad-drop flex items-center justify-between' style={timing(1100, 500)}>
                        <div className='flex items-center gap-[1.4cqw]'>
                            <span className='size-[3.4cqw] rounded-[0.9cqw] bg-volt' />
                            <span className='h-[1.5cqw] w-[10cqw] rounded-full bg-paper/70' />
                        </div>
                        <div className='flex items-center gap-[2.4cqw]'>
                            <span className='h-[1.2cqw] w-[6cqw] rounded-full bg-paper/30' />
                            <span className='h-[1.2cqw] w-[6cqw] rounded-full bg-paper/30' />
                            <span className='h-[1.2cqw] w-[6cqw] rounded-full bg-paper/30' />
                        </div>
                    </div>

                    <div className='mt-[5cqw] grid grid-cols-[1.15fr_1fr] items-center gap-[4cqw]'>
                        <div>
                            <span className='ad-a ad-wipe block h-[3.6cqw] w-[92%] rounded-[0.8cqw] bg-paper' style={timing(1400, 500)} />
                            <span className='ad-a ad-wipe mt-[1.6cqw] block h-[3.6cqw] w-[64%] rounded-[0.8cqw] bg-paper' style={timing(1550, 500)} />
                            <span className='ad-a ad-wipe mt-[2.6cqw] block h-[1.3cqw] w-[84%] rounded-full bg-paper/30' style={timing(1700, 500)} />
                            <span className='ad-a ad-wipe mt-[1.2cqw] block h-[1.3cqw] w-[58%] rounded-full bg-paper/30' style={timing(1780, 500)} />

                            <div className='ad-a ad-pop relative mt-[3.4cqw] inline-block' style={timing(1950, 550)}>
                                <span
                                    className='ad-a ad-press inline-flex items-center rounded-full bg-volt px-[3.2cqw] py-[1.5cqw] text-[length:max(8px,2.4cqw)] font-bold text-ink'
                                    style={timing(3550, 300, 'ease-in-out')}
                                >
                                    Book now
                                </span>
                                <span
                                    aria-hidden='true'
                                    className='ad-a ad-ripple pointer-events-none absolute left-[62%] top-[55%] size-[10cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-volt/60'
                                    style={timing(3550, 700, 'ease-out')}
                                />
                                <svg
                                    viewBox='0 0 24 24'
                                    aria-hidden='true'
                                    className='ad-a ad-cursor absolute left-[62%] top-[55%] size-[5cqw] drop-shadow-[0_0.4cqw_0.6cqw_rgba(0,0,0,0.5)] [--ad-from-x:24cqw] [--ad-from-y:16cqw]'
                                    style={timing(2800, 750, 'cubic-bezier(0.65, 0, 0.35, 1)')}
                                >
                                    <path d='M4 2.5 19.5 11 12.6 12.6 9.5 19.5Z' className='fill-paper stroke-ink [stroke-linejoin:round] [stroke-width:1.5]' />
                                </svg>
                            </div>
                        </div>

                        <div
                            className='ad-a ad-fade relative aspect-[16/11] overflow-hidden rounded-[2cqw] bg-linear-to-br from-volt via-amber-400 to-orange-500'
                            style={timing(1650, 600)}
                        >
                            <span className='absolute right-[14%] top-[16%] size-[6cqw] rounded-full bg-paper/90' />
                            <svg viewBox='0 0 100 60' preserveAspectRatio='none' aria-hidden='true' className='absolute inset-x-0 bottom-0 h-[55%] w-full'>
                                <path d='M0 60V34l22-20 18 16 16-12 44 30v12Z' className='fill-ink/70' />
                            </svg>
                        </div>
                    </div>

                    <div className='mt-[5cqw] grid grid-cols-3 gap-[2.6cqw]'>
                        {[0, 1, 2].map(index => (
                            <div
                                key={index}
                                className='ad-a ad-rise rounded-[1.6cqw] border border-paper/10 bg-paper/5 p-[2cqw]'
                                style={timing(2200 + index * 150)}
                            >
                                <span className='block size-[3.4cqw] rounded-full bg-volt/80' />
                                <span className='mt-[1.6cqw] block h-[1.2cqw] w-[80%] rounded-full bg-paper/50' />
                                <span className='mt-[1cqw] block h-[1cqw] w-[60%] rounded-full bg-paper/25' />
                            </div>
                        ))}
                    </div>

                    <div
                        className='ad-a ad-pop absolute right-[3.5cqw] top-[13cqw] flex items-center gap-[1.6cqw] rounded-[1.8cqw] bg-paper py-[1.6cqw] pl-[1.6cqw] pr-[3cqw] text-ink shadow-xl shadow-black/40'
                        style={timing(3850, 600)}
                    >
                        <span className='flex size-[5cqw] items-center justify-center rounded-full bg-volt'>
                            <CalendarCheck className='size-[58%]' aria-hidden='true' />
                        </span>
                        <span className='leading-tight'>
                            <span className='block text-[length:max(8px,2.4cqw)] font-bold'>
                                New booking
                            </span>
                            <span className='block text-[length:max(7px,1.8cqw)] text-ink/60'>
                                Just now
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

const BUILD_CHECKS = ['Designed around your brand', 'Sharp on every screen', 'Built to win customers']

const BuildScene: FC<AdvertSceneProps> = ({ duration }): ReactNode => {
    return (
        <SceneFrame duration={duration} className='flex flex-col justify-center gap-[6cqw] px-[6cqw] sm:grid sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)] sm:items-center sm:gap-[5cqw]'>
            <div>
                <p className='font-heading text-[length:7.4cqw] font-extrabold leading-[1.06] tracking-[-0.02em] text-paper sm:text-[length:4.6cqw]'>
                    <span className='ad-a ad-rise block' style={timing(150)}>
                        Designed.
                    </span>
                    <span className='ad-a ad-rise block' style={timing(450)}>
                        Built.
                    </span>
                    <span className='ad-a ad-rise block text-volt' style={timing(750)}>
                        Launched.
                    </span>
                </p>

                <ul className='mt-[4cqw] space-y-[2cqw] sm:mt-[2.6cqw] sm:space-y-[1.1cqw]'>
                    {BUILD_CHECKS.map((item, index) => (
                        <li
                            key={item}
                            className='ad-a ad-rise flex items-center gap-[2cqw] text-[length:max(11px,3.4cqw)] font-semibold text-paper/85 sm:gap-[1cqw] sm:text-[length:max(11px,1.6cqw)]'
                            style={timing(2300 + index * 300)}
                        >
                            <span className='flex size-[5cqw] shrink-0 items-center justify-center rounded-full bg-volt text-ink sm:size-[2.6cqw]'>
                                <Check className='size-[62%]' strokeWidth={3} aria-hidden='true' />
                            </span>
                            {item}
                        </li>
                    ))}
                </ul>
            </div>

            <BrowserMockup />
        </SceneFrame>
    )
}

const PriceScene: FC<AdvertSceneProps> = ({ duration }): ReactNode => {
    return (
        <SceneFrame duration={duration} className='flex flex-col items-center justify-center px-[6cqw] text-center'>
            <p
                className='ad-a ad-slam text-balance font-heading text-[length:7cqw] font-extrabold leading-[1.05] tracking-[-0.02em] text-paper sm:text-[length:5.6cqw]'
                style={timing(150, 650)}
            >
                Agency quality.
            </p>

            <div className='relative mt-[4cqw] sm:mt-[2.8cqw]'>
                <p
                    className='ad-a ad-rise font-heading text-[length:5.4cqw] font-bold text-paper/40 sm:text-[length:3.6cqw]'
                    style={timing(800)}
                >
                    Agency prices.
                </p>
                <span
                    aria-hidden='true'
                    className='ad-a ad-strike absolute -inset-x-[4%] top-1/2 h-[1cqw] -translate-y-1/2 -rotate-2 rounded-full bg-volt sm:h-[0.6cqw]'
                    style={timing(1350, 450, 'cubic-bezier(0.7, 0, 0.3, 1)')}
                />
            </div>

            <p
                className='ad-a ad-pop mt-[4cqw] text-balance font-heading text-[length:7cqw] font-extrabold leading-[1.05] tracking-[-0.02em] text-volt sm:mt-[2.8cqw] sm:text-[length:5.6cqw]'
                style={timing(1850, 650)}
            >
                Affordable prices.
            </p>

            <p
                className='ad-a ad-rise mt-[4cqw] max-w-[76cqw] text-balance text-[length:max(12px,3.6cqw)] font-medium text-paper/75 sm:mt-[2.6cqw] sm:text-[length:max(12px,1.8cqw)]'
                style={timing(2450)}
            >
                Priced for real-world budgets, whatever your size.
            </p>
        </SceneFrame>
    )
}

const BrandScene: FC<AdvertSceneProps> = ({ onQuote, onReplay }): ReactNode => {
    const actionClass = 'inline-flex items-center gap-[0.5em] rounded-full px-[1.4em] py-[0.75em] text-[length:max(12px,3.4cqw)] font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 sm:text-[length:max(12px,1.5cqw)]'

    return (
        <div className='absolute inset-0 flex flex-col items-center justify-center px-[6cqw] text-center'>
            <div
                aria-hidden='true'
                className='ad-glow absolute left-1/2 top-[45%] size-[90cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-volt/15 blur-[12cqw] sm:size-[62cqw]'
            />

            <div className='ad-a ad-pop relative' style={timing(150, 700)}>
                <LogoMark className='size-[20cqw] sm:size-[10.5cqw]' />
            </div>

            <div className='ad-a ad-wipe relative mt-[4cqw] sm:mt-[2.6cqw]' style={timing(650, 800, 'cubic-bezier(0.65, 0, 0.35, 1)')}>
                <LogoWordmark className='h-[12cqw] w-auto text-paper sm:h-[7cqw]' />
            </div>

            <div className='ad-a ad-rise relative mt-[2.4cqw] sm:mt-[1.6cqw]' style={timing(1150)}>
                <LogoDescriptor className='h-[3.35cqw] w-auto text-paper/65 sm:h-[1.95cqw]' />
            </div>

            <p
                className='ad-a ad-rise relative mt-[5cqw] text-balance font-heading text-[length:4.4cqw] font-semibold leading-snug text-paper sm:mt-[3cqw] sm:text-[length:2.5cqw]'
                style={timing(1650)}
            >
                Brilliant websites. <span className='text-volt'>Down-to-earth prices.</span>
            </p>

            <div
                className='ad-a ad-rise relative mt-[5cqw] flex flex-wrap items-center justify-center gap-[2.4cqw] sm:mt-[3cqw] sm:gap-[1.4cqw]'
                style={timing(2250)}
            >
                <a
                    href='#contact'
                    onClick={onQuote}
                    className={cn(actionClass, 'bg-volt text-ink hover:bg-paper focus-visible:outline-paper')}
                >
                    Get a quote
                    <ArrowRight className='size-[1.1em]' aria-hidden='true' />
                </a>
                <button
                    type='button'
                    onClick={onReplay}
                    className={cn(actionClass, 'border border-paper/25 text-paper hover:bg-paper/10 focus-visible:outline-volt')}
                >
                    <RotateCcw className='size-[1.05em]' aria-hidden='true' />
                    Watch again
                </button>
            </div>
        </div>
    )
}

export const SCENE_COMPONENTS: Record<AdvertSceneId, FC<AdvertSceneProps>> = {
    intro: IntroScene,
    local: LocalScene,
    big: BigScene,
    both: BothScene,
    build: BuildScene,
    price: PriceScene,
    brand: BrandScene
}
